// Consumables (shop supplies, laser nozzles, components): ONE copy of the reads, the search and the
// stock transaction (add / use / defective / count correction), used by the desktop Consumables page
// (pages/ConsumablesEntryPage.jsx) and the phone (mobile/screens/ConsumablesScreen.jsx). Actions
// take a `ui` (features/desktopUi.js on the desktop, mobile/ui/phoneUi.js on a phone): alert.
import config from "../../config.js";
import { apiErrorMessage } from "../../utils/apiError";

const API_BASE = config.API_BASE_URL;

// Reason value that marks a physical-count correction (jobq-api add_consumable_transaction:
// counted_quantity + count_correction).
export const COUNT_CORRECTION_REASON = "Count correction";

// The transaction reasons, in the desktop's order. `direction` is which way a phone's "how many"
// moves stock ('count' = the number counted on the shelf); the desktop types a signed delta.
export const TRANSACTION_REASONS = Object.freeze([
  Object.freeze({ value: "Add to inventory", label: "Add to inventory", direction: "in" }),
  Object.freeze({ value: "Defective", label: "Defective", direction: "out" }),
  Object.freeze({ value: "Consumed no job", label: "Consumed w/o job", direction: "out" }),
  Object.freeze({ value: COUNT_CORRECTION_REASON, label: "Count correction (physical count)", direction: "count" }),
]);

/** The sidebar search: product id, description or category contains `term` (any case). */
export function matchesConsumableSearch(c, term) {
  const t = String(term || "").toLowerCase();
  return (
    (c.product_id || "").toLowerCase().includes(t) ||
    (c.description || "").toLowerCase().includes(t) ||
    (c.category || "").toLowerCase().includes(t)
  );
}

/** GET /consumables (every item, or one consumable_type). Throws on failure. */
export async function loadConsumables(type = "all") {
  let url = `${API_BASE}/consumables`;
  if (type && type !== "all") url += `?consumable_type=${type}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to list consumables: ${res.status}`);
  return res.json();
}

/** GET /consumables/{id}. Throws on failure. */
export async function loadConsumable(consumableId) {
  const res = await fetch(`${API_BASE}/consumables/${consumableId}`);
  if (!res.ok) throw new Error("Failed to get consumable detail");
  return res.json();
}

/** GET /consumables/{id}/transactions (newest first), or null when it can't be read. */
export async function loadConsumableTransactions(consumableId) {
  try {
    const res = await fetch(`${API_BASE}/consumables/${consumableId}/transactions`);
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error("Failed to load consumable transactions:", err);
    return null;
  }
}

/**
 * GET /consumable/reorder_alerts: the items to order (available = on hand + inbound - reserved, at or
 * under the reorder point; recommended_qty from the API). Throws on failure.
 */
export async function loadReorderAlerts() {
  const res = await fetch(`${API_BASE}/consumable/reorder_alerts`);
  if (!res.ok) throw new Error("Failed to load consumable reorder alerts");
  return res.json();
}

/**
 * How an item stands against its reorder point, from its reorder-alert row (null = not listed):
 * {tone: 'danger' | 'ok' | 'muted', text, detail}. `alertsRead` false = the alerts couldn't be read
 * (or aren't in yet): never say "Stocked" then.
 */
export function reorderStatus(item, alertRow, alertsRead = true) {
  if (!alertsRead) return { tone: "muted", text: "Reorder status unavailable", detail: "the reorder alerts couldn't be read" };
  if (alertRow) {
    const parts = [`Available ${Number(alertRow.available) || 0}`];
    if (Number(alertRow.inbound_qty) > 0) parts.push(`${alertRow.inbound_qty} inbound`);
    if (Number(alertRow.reserved_quantity) > 0) parts.push(`${alertRow.reserved_quantity} reserved`);
    return { tone: "danger", text: `Order ${alertRow.recommended_qty}`, detail: parts.join(" · ") };
  }
  if (item && Number(item.reorder_point) > 0) {
    return { tone: "ok", text: "Stocked", detail: `Above its reorder point (${item.reorder_point})` };
  }
  return { tone: "muted", text: "No reorder point", detail: "" };
}

/**
 * A phone's "how many" (a count of pieces) as the quantity recordConsumableTransaction takes: a
 * signed delta for 'in' / 'out' reasons, the counted number for a count correction.
 * {quantity} or {problem} (the sentence to show).
 */
export function quantityFromCount(reason, count) {
  const r = TRANSACTION_REASONS.find((x) => x.value === reason);
  if (!r) return { problem: "Pick a reason." };
  const text = String(count ?? "").trim();
  if (r.direction === "count") {
    if (!/^\d+$/.test(text)) return { problem: "Enter the counted quantity (a whole number, 0 or more)." };
    return { quantity: String(parseInt(text, 10)) };
  }
  if (!/^\d+$/.test(text) || parseInt(text, 10) < 1) return { problem: "Enter how many (a whole number, 1 or more)." };
  const n = parseInt(text, 10);
  return { quantity: String(r.direction === "out" ? -n : n) };
}

/**
 * Record a stock transaction (POST /consumables/{id}/transactions). `quantity` is the signed delta,
 * or the COUNTED quantity when `reason` is a count correction: the API then works out the change
 * against live stock and keeps the row out of swap stats, the nozzle cost rate and the Slack alerts
 * (a plain negative delta is read as a nozzle swap everywhere). Says the outcome through `ui`.
 * Resolves {data} (the API answer: old_quantity, new_quantity, transaction_id), {cancelled} (nothing
 * sent) or {error}.
 */
export async function recordConsumableTransaction({ consumableId, reason, quantity, ui }) {
  if (!consumableId) {
    await ui.alert("Select or create an item first.");
    return { cancelled: true };
  }
  const isCount = reason === COUNT_CORRECTION_REASON;
  if (quantity === "" || quantity == null || !reason) {
    await ui.alert(isCount ? "Enter the counted quantity." : "Enter a delta and a reason.");
    return { cancelled: true };
  }
  const payload = isCount
    ? { counted_quantity: parseInt(quantity, 10), count_correction: true, reason }
    : { delta: parseInt(quantity, 10) || 0, reason };
  if (isCount && (!Number.isInteger(payload.counted_quantity) || payload.counted_quantity < 0)) {
    await ui.alert("The counted quantity must be a whole number, 0 or more.");
    return { cancelled: true };
  }
  try {
    const res = await fetch(`${API_BASE}/consumables/${consumableId}/transactions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await apiErrorMessage(res));
    const data = await res.json();
    if (isCount && data.transaction_id == null) {
      await ui.alert(`Count matches stock (${data.old_quantity}). Nothing recorded.`);
    } else if (isCount) {
      await ui.alert(`Count correction recorded: ${data.old_quantity} -> ${data.new_quantity}.`);
    } else {
      await ui.alert(`Transaction created. Old qty=${data.old_quantity}, new qty=${data.new_quantity}.`);
    }
    return { data };
  } catch (err) {
    await ui.alert("Failed to add transaction: " + err.message);
    return { error: err.message };
  }
}
